import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, RefreshCw, Loader2, LogOut } from 'lucide-react'
import { organizationApi } from '@/services/organization-api'
import { useOrgStore } from '@/stores/org-store'
import { useAuthStore } from '@/stores/auth-store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default function PendingApprovalPage() {
  const [message, setMessage] = useState('Your request is pending approval.')
  const [isChecking, setIsChecking] = useState(false)
  const navigate = useNavigate()
  const fetchOrgs = useOrgStore((s) => s.fetchOrgs)
  const { setOnboardingCompleted, signOut, user } = useAuthStore()

  const checkStatus = async () => {
    setIsChecking(true)
    try {
      const status = await organizationApi.onboardingStatus()
      if (status.has_active_org) {
        await fetchOrgs()
        setOnboardingCompleted(true)
        navigate('/app/dashboard')
      } else {
        setMessage('Still pending. Ask an admin to approve your request.')
      }
    } finally {
      setIsChecking(false)
    }
  }

  useEffect(() => {
    checkStatus()
  }, [])

  const handleLogout = async () => {
    await signOut()
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Top bar with user info and logout */}
      <div className="absolute top-4 right-4 flex items-center gap-3">
        {user?.email && (
          <span className="text-sm text-muted-foreground hidden sm:inline">
            {user.email}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Clock className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>Pending Approval</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <Button onClick={checkStatus} disabled={isChecking}>
            {isChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh Status
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Need to use a different account?{' '}
            <button 
              onClick={handleLogout}
              className="text-primary underline-offset-4 hover:underline"
            >
              Sign out
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
